/**
 * Created by camiel on 21/04/2017.
 */

import * as $ from "jquery";

import * as Promise from "bluebird";

import Bitmap = createjs.Bitmap;
import Container = createjs.Container;
import LoadQueue = createjs.LoadQueue;
import Sound = createjs.Sound;

import AssetManager from "lib/src/AssetManager";
import Spinner from "lib/src/Spinner";
import StageCanvas from "lib/src/StageCanvas";
import Utils from "lib/src/utils";

import { KEY_PRESSED_COLOR_OFFSET } from "src/constants";
import IColorOffset from "src/IColorOffset";
import InstrumentButton from "src/InstrumentButton";
import KeyboardKey, {
  KEYBOARD_KEY_WHITE_HEIGHT,
  KEYBOARD_KEY_WHITE_WIDTH,
} from "src/KeyboardKey";
import KeyboardSounds, { InstrumentId, KeyboardKeyId, keyCodeToKeyId } from "src/KeyboardSounds";
import LoopButton, {
  LOOP_BUTTON_HEIGHT,
  LOOP_BUTTON_PADDING,
  LOOP_BUTTON_WIDTH,
  LOOP_IDS,
} from "src/LoopButton";
import SoundManager from "lib/src/SoundManager";
import WebLaunchHelper from "lib/src/WebLaunchHelper";

const BACKGROUND_ID = "background";
const BACKGROUND_UPPER_ID = "background-upper";

const KEYBOARD_PADDING = 20;
const KEYBOARD_KEY_WHITE_SPACING = 5;

function initializeKeyboardSoundsMap(): object {
  const keyboardSoundsMap = { } as any;

  KeyboardSounds
    .instrumentIds
    .forEach((instrumentId) => {
      keyboardSoundsMap[instrumentId] = new KeyboardSounds(instrumentId);
    });

  return Object.freeze(keyboardSoundsMap);
}

export default class Keyboard {
  private readonly _stageCanvas: StageCanvas;
  private readonly _preLoadQueue: LoadQueue;
  private readonly _content: Container;
  private readonly _whiteKeysContainer: Container;
  private readonly _blackKeysContainer: Container;
  private readonly _loopButtons: LoopButton[] = [];
  private _keyboardSoundsMap: object;

  private _spinner: Spinner;
  private _background: Bitmap;
  private _backgroundUpper: Bitmap;
  private _backgroundUpperContainer: Container;
  private _instrumentButtons: ReadonlyArray<InstrumentButton> = Object.freeze([ ]);
  private _instrumentId: InstrumentId = KeyboardSounds.instrumentIds[0];

  constructor() {
    const $window = $(window);

    const windowWidth = $window.width();
    const windowHeight = $window.height();

    this._stageCanvas = new StageCanvas(windowWidth, windowHeight);
    this._whiteKeysContainer = new Container();
    this._blackKeysContainer = new Container();
    this._backgroundUpperContainer = new Container();

    this._preLoadQueue = new createjs.LoadQueue(false);
    createjs.Sound.alternateExtensions = [ "mp3" ];
    this._preLoadQueue.installPlugin(Sound);

    this._content = new Container();

    WebLaunchHelper.init();
  }

  public start(): Promise<{ }> {
    // TODO: controlled updates
    this._stageCanvas.mount(true);

    document.addEventListener("visibilitychange", () => {
      this._handleVisibilityChange();
    }, false);

    return new Promise((resolve) => {
      Spinner.initializeLoadingSpinner(
        this._stageCanvas.stage,
        this._stageCanvas.stage,
        (spinner) => {

          this._spinner = spinner;
          this._spinner.showCentered();

          // call async, so the spinner is already shown
          window.setTimeout(() => {
            resolve(this._startLoading());
          }, 100);
        },
      );
    });
  }

  public stopAllLoops() {
    this._loopButtons.forEach((btn) => {
      btn.stopLoop();
    });
  }

  private _handleVisibilityChange() {
    console.log('handleVisibilityChange, hidden = ' + document.hidden + ', visibilityState = ' + document.visibilityState);
    if (document.hidden) {
      // stop loops, we're backgrounded
      this.stopAllLoops();
    }
  }

  private _startLoading(): Promise<{ }> {
    const manifest = [ ];

    KeyboardKey.addToManifest(manifest);
    InstrumentButton.addToManifest(manifest);
    LoopButton.addToManifest(manifest);

    this._keyboardSoundsMap = initializeKeyboardSoundsMap();

    manifest.push({
      src: AssetManager.getShared('sound/kies_zelf_een_instrument_en_bespeel_deze_op_de_piano.mp3'),
      id: 'sound_intro'
    });

    const assets = [
      BACKGROUND_ID,
      BACKGROUND_UPPER_ID,
    ];

    assets
      .map((id) => ({
        id,
        src: AssetManager.getForChar(`${id}.png`),
      }))
      .forEach((asset) => manifest.push(asset));

    return new Promise((resolve) => {
      this._preLoadQueue.on("complete", () => {
        this._onLoaded();
        resolve();
      });

      this._preLoadQueue.loadManifest(manifest);
    });
  }

  private _onLoaded(): void {
    this._spinner.hideNow();

    SoundManager.playSound('sound_intro');

    this._initializeBackground();

    this._content.addChild(this._background);

    this._initializeWhiteKeys();
    this._initializeBlackKeys();

    this._content.addChild(this._backgroundUpperContainer);
    this._initializeInstrumentButtons();
    this._selectInstrumentId(this._instrumentId, false);

    this._initializeLoopButtons();

    this._stageCanvas.stage.addChild(this._content);
  }

  private _initializeWhiteKeys(): void {
    this._initializeKeys(
      KeyboardSounds.whiteKeyIds,
      this._background.image.height - KEYBOARD_KEY_WHITE_HEIGHT - 40,
      { red: 255, green: 255, blue: 255 },
      KEY_PRESSED_COLOR_OFFSET,
      (index) => KEYBOARD_KEY_WHITE_WIDTH * (0.5 + index) + KEYBOARD_KEY_WHITE_SPACING * index,
    );
  }

  private _initializeBlackKeys(): void {
    this._initializeKeys(
      KeyboardSounds.blackKeyIds,
      this._background.image.height - KEYBOARD_KEY_WHITE_HEIGHT,
      { red: 0, green: 0, blue: 0 },
      { red: 77, green: 77, blue: 77 },
      (index) => {
        let letOffsetIndex = index;

        if (index > 1) {
          letOffsetIndex += 1;
        }

        if (index > 4) {
          letOffsetIndex += 1;
        }

        return KEYBOARD_KEY_WHITE_WIDTH +
          (KEYBOARD_KEY_WHITE_WIDTH + KEYBOARD_KEY_WHITE_SPACING) * letOffsetIndex;
      },
    );
  }

  private _initializeKeys(
    keyboardKeyIds: ReadonlyArray<KeyboardKeyId>,
    y: number,
    colorOffset: IColorOffset,
    pressedColorOffset: IColorOffset,
    getKeyXOffset: (index: number) => number,
  ): void {
    const playSound = this._playSound.bind(this);

    const keyboardKeysContainer = new Container();

    keyboardKeysContainer.regX = KEYBOARD_KEY_WHITE_WIDTH * KeyboardSounds.whiteKeyIds.length / 2;

    keyboardKeysContainer.x = this._background.image.width / 2 - 12;
    keyboardKeysContainer.y = y;

    keyboardKeyIds
      .forEach((keyboardKeyId, index) => {
        const keyboardKey = new KeyboardKey(
          keyboardKeyId,
          this._preLoadQueue,
          playSound,
          colorOffset,
          pressedColorOffset,
        );

        keyboardKey.container.x = getKeyXOffset(index);
        keyboardKey.container.y = 0;

        keyboardKeysContainer.addChild(keyboardKey.container);
      });

    this._content.addChild(keyboardKeysContainer);
  }

  private _initializeInstrumentButtons(): void {
    const xOffset = this._backgroundUpper.image.width / (KeyboardSounds.instrumentIds.length);
    const onClick = (instrumentId: InstrumentId) => {
      this._selectInstrumentId(instrumentId, true);
    };

    const instrumentButtons = KeyboardSounds
      .instrumentIds
      .map((instrumentId, index) => {
        const instrumentButton = new InstrumentButton(
          instrumentId,
          this._preLoadQueue,
          onClick,
        );

        instrumentButton.container.x =
          -this._backgroundUpper.image.width / 2 + (index + 0.5) * xOffset;
        instrumentButton.container.y = 165;

        this._backgroundUpperContainer.addChild(instrumentButton.container);

        return instrumentButton;
      });

    this._instrumentButtons = Object.freeze(instrumentButtons);
  }

  private _initializeLoopButtons(): void {
    const loopButtonsContainer = new Container();

    loopButtonsContainer.x = this._content.globalToLocal(this._stageCanvas.width, 0).x;
    loopButtonsContainer.y = 0;
    loopButtonsContainer.regY = -(this._background.image.height + KEYBOARD_PADDING * 2) / 2;

    const offsetY = -(
        LOOP_IDS.length * LOOP_BUTTON_HEIGHT + (LOOP_IDS.length - 1) * LOOP_BUTTON_PADDING
      ) / 2 + LOOP_BUTTON_HEIGHT / 2;

    LOOP_IDS.forEach((loopId, i) => {
      const loopButton = new LoopButton(
        loopId,
        this._preLoadQueue,
        this._playLoop.bind(this),
      );

      loopButton.container.x = 0;
      loopButton.container.y =
        (LOOP_BUTTON_HEIGHT + LOOP_BUTTON_PADDING) * i + offsetY;
      loopButtonsContainer.addChild(loopButton.container);

      this._loopButtons.push(loopButton);
    });

    this._content.addChild(loopButtonsContainer);
  }

  private _playLoop(loopId: string) {
    this._loopButtons.forEach((loopButton) => {
      if (loopButton.loopId !== loopId) {
        loopButton.stopLoop();
      } else {
        loopButton.toggleLoop();
      }
    });
  }

  private _playSound(keyboardKeyId: KeyboardKeyId): void {
    this._keyboardSounds.playSound(keyboardKeyId);
  }

  private _selectInstrumentId(instrumentId: InstrumentId, animated: boolean): void {
    this._instrumentId = instrumentId;

    for (const instrumentButton of this._instrumentButtons) {
      instrumentButton.setActive(instrumentButton.instrumentId === instrumentId, animated);
    }
  }

  private get _keyboardSounds(): KeyboardSounds {
    return this._keyboardSoundsMap[this._instrumentId];
  }

  private _initializeBackground(): void {
    const backgroundImage = this._preLoadQueue.getResult(BACKGROUND_ID) as HTMLImageElement;

    this._background = new Bitmap(backgroundImage);

    const loopButtonsWidth = LOOP_BUTTON_WIDTH;

    const contentWidth = backgroundImage.width + KEYBOARD_PADDING * 2 + loopButtonsWidth;
    const contentHeight = backgroundImage.height + KEYBOARD_PADDING * 2;

    Utils.centerBitmap(this._background);

    this._background.x = (contentWidth - loopButtonsWidth) / 2;
    this._background.y = contentHeight / 2;

    const backgroundUpperImage =
      this._preLoadQueue.getResult(BACKGROUND_UPPER_ID) as HTMLImageElement;
    this._backgroundUpper = new Bitmap(backgroundUpperImage);

    this._backgroundUpper.regX = backgroundUpperImage.width / 2;
    this._backgroundUpper.regY = 0;

    // needed for background asset alignment
    const backgroundUpperScale = 0.995;
    this._backgroundUpper.scaleX = backgroundUpperScale;
    this._backgroundUpper.scaleY = backgroundUpperScale;

    this._backgroundUpperContainer.x = (contentWidth - loopButtonsWidth) / 2 - 5;
    this._backgroundUpperContainer.y = 75;

    this._backgroundUpperContainer.addChild(this._backgroundUpper);

    const scale = Math.min(
      this._stageCanvas.width / contentWidth,
      this._stageCanvas.height / contentHeight,
    );

    this._content.scaleX = scale;
    this._content.scaleY = scale;
    this._content.regX = contentWidth / 2;
    this._content.regY = contentHeight / 2;
    this._content.x = this._stageCanvas.width / 2;
    this._content.y = this._stageCanvas.height / 2;
  }
}



// WEBPACK FOOTER //
// ./src/Keyboard.ts